import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactNode, RefObject } from "react";
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useScrollThumb } from "hooks/useScrollThumb";

export type SelectOption = {
  value: string;
  label: string;
  /** Rendered before the label — a flag or a product cube. */
  icon?: ReactNode;
  /** Rendered right-aligned inside the option, e.g. a dial code. */
  hint?: string;
  /** A heading drawn above the first option of each group (`.pcsel__grp`). */
  group?: string;
};

type SelectProps = {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  /** Shown on the button while nothing is selected. */
  placeholder?: string;
  /** `--code` for the dial picker, `--cube` for the product picker. */
  variant?: "code" | "cube";
  /** Button label override; defaults to the selected option's label. */
  buttonLabel?: string;
  /**
   * Element the list must stay inside — pass the modal or panel the select
   * sits in. Defaults to the viewport.
   */
  boundsRef?: RefObject<HTMLElement | null>;
  searchable?: boolean;
  /** The design's per-field modifier, e.g. `pcsel--dg`. */
  className?: string;
  /** Marks the field invalid, as `.pcsel.is-bad` does. */
  invalid?: boolean;
};

/** The list height the design draws. */
const MAX_LIST_HEIGHT = 250;

/** Breathing room between the list and the edge it is measured against. */
const EDGE_GAP = 12;

/**
 * Below this the list is too short to be worth reading, and flipping it up is
 * better than three visible options — even though that covers the fields above.
 */
const MIN_LIST_HEIGHT = 160;

type Placement = { up: boolean; maxHeight: number };

const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const digitsOnly = (value: string): string => value.replace(/\D/g, "");

const optionMatchesQuery = (option: SelectOption, query: string): boolean => {
  const rawQuery = query.trim();
  if (!rawQuery) {
    return true;
  }

  const textQuery = normalizeText(rawQuery);
  const label = normalizeText(option.label);
  const hint = normalizeText(option.hint ?? "");

  if (label.includes(textQuery) || hint.includes(textQuery)) {
    return true;
  }

  const queryDigits = digitsOnly(rawQuery);
  const hintDigits = digitsOnly(option.hint ?? "");

  return Boolean(queryDigits && hintDigits.includes(queryDigits));
};

/**
 * The design's custom dropdown: a button plus an absolutely positioned list.
 * A native `<select>` cannot carry the flags and cubes the design puts in it.
 */
export const Select = ({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  variant,
  buttonLabel,
  boundsRef,
  searchable = false,
  className,
  invalid = false,
}: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [placement, setPlacement] = useState<Placement>({ up: false, maxHeight: MAX_LIST_HEIGHT });
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  // The list only exists while open, so the thumb binds each time it opens.
  const { listRef: optsRef, thumbRef } = useScrollThumb(open);

  const selected = options.find((option) => option.value === value);

  const filteredOptions = useMemo(
    () => (searchable ? options.filter((option) => optionMatchesQuery(option, query)) : options),
    [options, query, searchable]
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  /**
   * The list stays inside `boundsRef`, so it never hangs out of a modal and
   * over the page behind it. It opens downward, as the design draws it, and
   * flips up only when there is too little room below to show a usable list.
   */
  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const measure = () => {
      const anchor = wrapRef.current;

      if (anchor === null) return;

      const bounds = boundsRef?.current?.getBoundingClientRect();
      const top = bounds?.top ?? 0;
      const bottom = bounds?.bottom ?? window.innerHeight;
      const rect = anchor.getBoundingClientRect();

      const below = bottom - rect.bottom - EDGE_GAP;
      const above = rect.top - top - EDGE_GAP;
      const up = below < MIN_LIST_HEIGHT && above > below;

      setPlacement({ up, maxHeight: Math.min(MAX_LIST_HEIGHT, Math.max(up ? above : below, 0)) });
    };

    measure();

    window.addEventListener("resize", measure);
    // Capturing: the modal, not the window, is what scrolls under the list.
    window.addEventListener("scroll", measure, true);

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [boundsRef, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (searchable) {
      searchInputRef.current?.focus();
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open, searchable]);

  const pick = (nextValue: string) => {
    onChange(nextValue);
    close();
  };

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    const firstMatch = filteredOptions[0];
    if (firstMatch) {
      pick(firstMatch.value);
    }
  };

  const listStyle: CSSProperties = { maxHeight: `${placement.maxHeight}px` };

  return (
    <div
      ref={wrapRef}
      className={cn("pcsel", className, {
        "is-bad": invalid,
        "pcsel--code": variant === "code",
        "pcsel--cube": variant === "cube",
        "pcsel--searchable": searchable,
        "is-open": open,
      })}
    >
      <button
        type="button"
        className="pcsel__btn"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {selected?.icon && <span className="pcsel__flag">{selected.icon}</span>}
        <span className="pcsel__txt">{buttonLabel ?? selected?.label ?? placeholder ?? ""}</span>
        <i className="pcsel__ch" />
      </button>

      {open && (
        <div
          className={cn("pcsel__list", { "pcsel__list--up": placement.up })}
          style={listStyle}
          id={listId}
          role="listbox"
        >
          {searchable && (
            <div className="pcsel__search">
              <input
                ref={searchInputRef}
                type="text"
                className="pcsel__search-input"
                autoComplete="off"
                value={query}
                placeholder="Search country or code"
                aria-label="Search country or code"
                onChange={(event) => setQuery(event.currentTarget.value)}
                onKeyDown={handleSearchKeyDown}
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          )}
          {/* The design's scroll rule: no native bar, a drawn thumb instead. */}
          <div className="pcsel__optw">
            <div className="pcsel__opts" ref={optsRef}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <Fragment key={option.value}>
                    {option.group && option.group !== filteredOptions[index - 1]?.group && (
                      <div className="pcsel__grp">{option.group}</div>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      className={cn("pcsel__opt", { "is-on": option.value === value })}
                      onClick={() => pick(option.value)}
                    >
                      {option.icon}
                      <span>{option.label}</span>
                      {option.hint && <em>{option.hint}</em>}
                    </button>
                  </Fragment>
                ))
              ) : (
                <div className="pcsel__empty">No countries found</div>
              )}
            </div>
            <div className="pcsel__thumb" ref={thumbRef} />
          </div>
        </div>
      )}
    </div>
  );
};
